import { supabase } from "@/integrations/supabase/client";

export interface TitleIXMetrics {
  academicYear: string;
  maleEnrollment: number;
  femaleEnrollment: number;
  maleRevenueSharing: number;
  femaleRevenueSharing: number;
  maleAthletes: number;
  femaleAthletes: number;
  complianceStatus: "compliant" | "at_risk" | "non_compliant" | "under_review";
  variancePercentage: number;
}

export interface TitleIXAnalysis {
  enrollmentRatio: number;
  revenueRatio: number;
  athleteRatio: number;
  revenuePerMaleAthlete: number;
  revenuePerFemaleAthlete: number;
  isCompliant: boolean;
  warnings: string[];
  recommendations: string[];
}

export function calculateTitleIXCompliance(metrics: TitleIXMetrics): TitleIXAnalysis {
  const warnings: string[] = [];
  const recommendations: string[] = [];

  // Calculate ratios
  const totalEnrollment = metrics.maleEnrollment + metrics.femaleEnrollment;
  const enrollmentRatio = totalEnrollment > 0 
    ? (metrics.femaleEnrollment / totalEnrollment) * 100 
    : 50;

  const totalRevenue = metrics.maleRevenueSharing + metrics.femaleRevenueSharing;
  const revenueRatio = totalRevenue > 0 
    ? (metrics.femaleRevenueSharing / totalRevenue) * 100 
    : 50;

  const totalAthletes = metrics.maleAthletes + metrics.femaleAthletes;
  const athleteRatio = totalAthletes > 0 
    ? (metrics.femaleAthletes / totalAthletes) * 100 
    : 50;

  const revenuePerMaleAthlete = metrics.maleAthletes > 0 
    ? metrics.maleRevenueSharing / metrics.maleAthletes 
    : 0;

  const revenuePerFemaleAthlete = metrics.femaleAthletes > 0 
    ? metrics.femaleRevenueSharing / metrics.femaleAthletes 
    : 0;

  // Variance calculation (difference between enrollment ratio and revenue ratio)
  const variance = Math.abs(enrollmentRatio - revenueRatio);

  // Title IX compliance checks
  // 1. Proportionality test: Revenue distribution should match enrollment within 5%
  if (variance > 5) {
    warnings.push(`Revenue distribution variance is ${variance.toFixed(1)}% (>5% threshold)`);
    
    if (revenueRatio < enrollmentRatio) {
      recommendations.push(
        `Increase female revenue sharing by approximately $${
          ((enrollmentRatio / 100) * totalRevenue - metrics.femaleRevenueSharing).toLocaleString()
        } to match enrollment ratio`
      );
    } else {
      recommendations.push(
        `Consider adjusting revenue distribution to better align with enrollment ratios`
      );
    }
  }

  // 2. Per-athlete equity check (within 10% is acceptable)
  if (revenuePerMaleAthlete > 0 && revenuePerFemaleAthlete > 0) {
    const perAthleteVariance = Math.abs(
      ((revenuePerMaleAthlete - revenuePerFemaleAthlete) / revenuePerMaleAthlete) * 100
    );
    
    if (perAthleteVariance > 10) {
      warnings.push(
        `Per-athlete revenue disparity is ${perAthleteVariance.toFixed(1)}% (>10% threshold)`
      );
      recommendations.push(
        `Review revenue distribution to ensure equitable per-athlete compensation`
      );
    }
  }

  // 3. Athlete participation check
  const athleteVariance = Math.abs(enrollmentRatio - athleteRatio);
  if (athleteVariance > 10) {
    warnings.push(
      `Athlete participation variance is ${athleteVariance.toFixed(1)}% from enrollment ratio`
    );
  }

  // Determine compliance status
  let isCompliant = true;
  if (variance > 10 || warnings.length >= 2) {
    isCompliant = false;
  }

  return {
    enrollmentRatio,
    revenueRatio,
    athleteRatio,
    revenuePerMaleAthlete,
    revenuePerFemaleAthlete,
    isCompliant,
    warnings,
    recommendations,
  };
}

export async function updateTitleIXCompliance(
  academicYear: string,
  metrics: Partial<TitleIXMetrics>
): Promise<void> {
  const fullMetrics: TitleIXMetrics = {
    academicYear,
    maleEnrollment: metrics.maleEnrollment || 0,
    femaleEnrollment: metrics.femaleEnrollment || 0,
    maleRevenueSharing: metrics.maleRevenueSharing || 0,
    femaleRevenueSharing: metrics.femaleRevenueSharing || 0,
    maleAthletes: metrics.maleAthletes || 0,
    femaleAthletes: metrics.femaleAthletes || 0,
    complianceStatus: "under_review",
    variancePercentage: 0,
  };

  const analysis = calculateTitleIXCompliance(fullMetrics);

  // Determine compliance status based on analysis
  let complianceStatus: TitleIXMetrics["complianceStatus"];
  if (analysis.isCompliant) {
    complianceStatus = "compliant";
  } else if (analysis.warnings.length === 1) {
    complianceStatus = "at_risk";
  } else {
    complianceStatus = "non_compliant";
  }

  const { error } = await supabase
    .from("title_ix_compliance" as any)
    .upsert({
      academic_year: academicYear,
      male_enrollment: fullMetrics.maleEnrollment,
      female_enrollment: fullMetrics.femaleEnrollment,
      male_revenue_sharing: fullMetrics.maleRevenueSharing,
      female_revenue_sharing: fullMetrics.femaleRevenueSharing,
      male_athletes: fullMetrics.maleAthletes,
      female_athletes: fullMetrics.femaleAthletes,
      compliance_status: complianceStatus,
      variance_percentage: Math.abs(analysis.enrollmentRatio - analysis.revenueRatio),
    });

  if (error) throw error;
}

export async function calculatePoolImpactOnTitleIX(
  poolGender: "male" | "female" | "coed",
  poolAmount: number,
  academicYear: string
): Promise<{ projectedCompliance: TitleIXAnalysis; impactSummary: string }> {
  // Fetch current Title IX data
  const { data: currentData } = await supabase
    .from("title_ix_compliance" as any)
    .select("*")
    .eq("academic_year", academicYear)
    .single();

  if (!currentData) {
    return {
      projectedCompliance: {
        enrollmentRatio: 0,
        revenueRatio: 0,
        athleteRatio: 0,
        revenuePerMaleAthlete: 0,
        revenuePerFemaleAthlete: 0,
        isCompliant: false,
        warnings: ["No Title IX data available for this academic year"],
        recommendations: ["Create Title IX compliance record first"],
      },
      impactSummary: "No baseline data available",
    };
  }

  const data = currentData as any;

  // Project the impact
  const projectedMetrics: TitleIXMetrics = {
    academicYear,
    maleEnrollment: data.male_enrollment,
    femaleEnrollment: data.female_enrollment,
    maleRevenueSharing: poolGender === "male" || poolGender === "coed" 
      ? data.male_revenue_sharing + (poolGender === "coed" ? poolAmount / 2 : poolAmount)
      : data.male_revenue_sharing,
    femaleRevenueSharing: poolGender === "female" || poolGender === "coed"
      ? data.female_revenue_sharing + (poolGender === "coed" ? poolAmount / 2 : poolAmount)
      : data.female_revenue_sharing,
    maleAthletes: data.male_athletes,
    femaleAthletes: data.female_athletes,
    complianceStatus: "under_review",
    variancePercentage: 0,
  };

  const projectedCompliance = calculateTitleIXCompliance(projectedMetrics);

  const currentVariance = Math.abs(
    (data.female_enrollment / (data.male_enrollment + data.female_enrollment)) * 100 -
    (data.female_revenue_sharing / (data.male_revenue_sharing + data.female_revenue_sharing)) * 100
  );

  const projectedVariance = Math.abs(
    projectedCompliance.enrollmentRatio - projectedCompliance.revenueRatio
  );

  const impactSummary = projectedVariance < currentVariance
    ? `✓ Improves compliance (variance: ${currentVariance.toFixed(1)}% → ${projectedVariance.toFixed(1)}%)`
    : `⚠ May worsen compliance (variance: ${currentVariance.toFixed(1)}% → ${projectedVariance.toFixed(1)}%)`;

  return {
    projectedCompliance,
    impactSummary,
  };
}
